import { useState } from "react";
import { Settings, Save, Plus } from "lucide-react";
import { usePricingRule } from "@/hooks/useBilling";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type { PricingRule } from "@/types/billing";

interface PricingRuleFormData {
  name: string;
  price_per_vcpu: number;
  price_per_gb_ram: number;
  price_per_gb_disk: number;
  tier_a_multiplier: number;
  tier_b_multiplier: number;
  tier_c_multiplier: number;
  price_network_100m: number;
  price_network_500m: number;
  price_network_1g: number;
  price_backup_snapshot: number;
  price_backup_full: number;
  price_backup_incremental: number;
}

const FIELD_LABELS: { key: keyof PricingRuleFormData; label: string; hint: string }[] = [
  { key: "price_per_vcpu", label: "Price per vCPU", hint: "EUR/month per core" },
  { key: "price_per_gb_ram", label: "Price per GB RAM", hint: "EUR/month per GB" },
  { key: "price_per_gb_disk", label: "Price per GB Disk", hint: "EUR/month per GB" },
  { key: "tier_a_multiplier", label: "Tier A Multiplier", hint: "Standalone (default 1.0)" },
  { key: "tier_b_multiplier", label: "Tier B Multiplier", hint: "Active/Passive (default 1.5)" },
  { key: "tier_c_multiplier", label: "Tier C Multiplier", hint: "Active/Active (default 2.0)" },
  { key: "price_network_100m", label: "Network 100 Mbit/s", hint: "EUR/month" },
  { key: "price_network_500m", label: "Network 500 Mbit/s", hint: "EUR/month" },
  { key: "price_network_1g", label: "Network 1 Gbit/s", hint: "EUR/month" },
  { key: "price_backup_snapshot", label: "Snapshot Backup", hint: "EUR/month" },
  { key: "price_backup_full", label: "Full Backup", hint: "EUR/month" },
  { key: "price_backup_incremental", label: "Incremental Backup", hint: "EUR/month" },
];

function ruleToForm(rule: PricingRule): PricingRuleFormData {
  return {
    name: rule.name,
    price_per_vcpu: rule.price_per_vcpu,
    price_per_gb_ram: rule.price_per_gb_ram,
    price_per_gb_disk: rule.price_per_gb_disk,
    tier_a_multiplier: rule.tier_a_multiplier,
    tier_b_multiplier: rule.tier_b_multiplier,
    tier_c_multiplier: rule.tier_c_multiplier,
    price_network_100m: rule.price_network_100m,
    price_network_500m: rule.price_network_500m,
    price_network_1g: rule.price_network_1g,
    price_backup_snapshot: rule.price_backup_snapshot,
    price_backup_full: rule.price_backup_full,
    price_backup_incremental: rule.price_backup_incremental,
  };
}

const DEFAULT_FORM: PricingRuleFormData = {
  name: "Standard",
  price_per_vcpu: 5,
  price_per_gb_ram: 2.5,
  price_per_gb_disk: 0.1,
  tier_a_multiplier: 1.0,
  tier_b_multiplier: 1.5,
  tier_c_multiplier: 2.0,
  price_network_100m: 0,
  price_network_500m: 5,
  price_network_1g: 10,
  price_backup_snapshot: 3,
  price_backup_full: 8,
  price_backup_incremental: 5,
};

/**
 * Admin component for viewing and editing the active pricing rule.
 *
 * Displays the current pricing configuration (unit prices per vCPU, GB RAM,
 * GB disk, tier multipliers, and backup plan costs) in a read-only summary.
 * Admins can click "Update Pricing" to switch to an inline editing form
 * that posts the updated rule to the backend via `POST /billing/pricing`.
 *
 * On successful save the pricing-rule query cache is invalidated so that
 * other components (e.g. `PricingSummaryPanel`) pick up the new values.
 */
export function PricingRuleEditor() {
  const { data: currentRule, isLoading } = usePricingRule();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<PricingRuleFormData>(DEFAULT_FORM);
  const queryClient = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: async (data: PricingRuleFormData) => {
      const { data: result } = await apiClient.post<PricingRule>(
        "/billing/pricing",
        data
      );
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pricing-rule"] });
      setEditing(false);
    },
  });

  const startEditing = () => {
    setForm(currentRule ? ruleToForm(currentRule) : DEFAULT_FORM);
    setEditing(true);
  };

  const updateField = (key: keyof PricingRuleFormData, value: string) => {
    if (key === "name") {
      setForm((prev) => ({ ...prev, name: value }));
    } else {
      setForm((prev) => ({ ...prev, [key]: parseFloat(value) || 0 }));
    }
  };

  if (isLoading) return null;

  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-blue-400" />
          <h2 className="text-lg font-semibold text-gray-200">
            Pricing Configuration
          </h2>
        </div>
        {!editing && (
          <button
            onClick={startEditing}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-3 py-1.5 text-sm text-gray-300 transition-colors hover:bg-gray-800"
          >
            <Plus className="h-4 w-4" />
            {currentRule ? "Update Pricing" : "Configure Pricing"}
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="mb-1 block text-sm text-gray-400">
              Rule Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => updateField("name", e.target.value)}
              className="w-full max-w-xs rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
            />
          </div>

          {/* Price fields grid */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {FIELD_LABELS.map(({ key, label, hint }) => (
              <div key={key}>
                <label className="mb-1 block text-sm text-gray-400">
                  {label}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form[key]}
                    onChange={(e) => updateField(key, e.target.value)}
                    className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <p className="mt-0.5 text-xs text-gray-500">{hint}</p>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={() => saveMutation.mutate(form)}
              disabled={saveMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {saveMutation.isPending ? "Saving..." : "Save Pricing Rule"}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:bg-gray-800"
            >
              Cancel
            </button>
          </div>

          {saveMutation.isError && (
            <p className="text-sm text-red-400">
              Failed to save: {saveMutation.error.message}
            </p>
          )}
        </div>
      ) : currentRule ? (
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm md:grid-cols-3">
          <div>
            <span className="text-gray-500">Rule</span>
            <p className="font-medium text-gray-200">{currentRule.name}</p>
          </div>
          {FIELD_LABELS.map(({ key, label }) => (
            <div key={key}>
              <span className="text-gray-500">{label}</span>
              <p className="font-mono text-gray-200">
                {typeof currentRule[key] === "number"
                  ? currentRule[key].toFixed(2)
                  : currentRule[key]}
              </p>
            </div>
          ))}
          <div>
            <span className="text-gray-500">Active since</span>
            <p className="text-gray-200">
              {new Date(currentRule.effective_from).toLocaleDateString("fr-FR")}
            </p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-400">
          No pricing rule configured yet. Click "Configure Pricing" to set
          prices.
        </p>
      )}
    </div>
  );
}
